import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { appConfig } from "../api";
import logo from "../assets/img/elite-logo.png";

const formatDate = (value) => {
  if (!value) return "__________________";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "__________________" : date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStoredGrcBooking = (search) => {
  const params = new URLSearchParams(search);
  const key = params.get("key");

  if (!key) return null;

  try {
    const rawBooking = window.localStorage.getItem(`grc:${key}`);
    return rawBooking ? JSON.parse(rawBooking) : null;
  } catch (error) {
    console.error("Unable to load GRC booking:", error);
    return null;
  }
};

const GrcForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const booking = location.state?.booking || getStoredGrcBooking(location.search);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!booking || params.get("print") !== "1") return undefined;

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 400);

    return () => window.clearTimeout(printTimer);
  }, [booking, location.search]);

  if (!booking) {
    return (
      <main className="min-h-screen bg-slate-100 flex items-center justify-center px-4">
        <section className="bg-white border border-slate-200 rounded-lg p-8 max-w-md text-center shadow-sm">
          <h1 className="text-xl font-semibold text-slate-900">GRC Unavailable</h1>
          <p className="text-sm text-slate-500 mt-2">
            Please open the GRC form again from the bookings page.
          </p>
          <button
            type="button"
            onClick={() => navigate("/adminhome")}
            className="mt-5 px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium"
          >
            Back to Admin
          </button>
        </section>
      </main>
    );
  }

  const paymentSources = [
    "MMT",
    "Booking.com",
    "Expedia",
    "Corporate",
    "Goibibo",
    "Agoda",
    "Yatra",
    "Walk-in / Direct",
  ];

  return (
    <main className="min-h-screen bg-slate-100 py-6 px-4 print:bg-white print:p-0">
      <style>{`
        @page {
          size: A4 portrait;
          margin: 10mm;
        }

        @media print {
          .no-print {
            display: none !important;
          }

          .grc-sheet {
            box-shadow: none !important;
            border: 1px solid #333 !important;
            width: 100% !important;
            max-width: none !important;
            padding: 20px !important;
            margin: 0 !important;
          }

          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
          }
        }
      `}</style>

      <div className="no-print max-w-4xl mx-auto mb-4 flex justify-between items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-md border border-slate-300 bg-white text-slate-700 text-sm font-medium hover:bg-slate-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={() => window.print()}
          className="px-5 py-2 rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
        >
          Print GRC Form
        </button>
      </div>

      <section className="grc-sheet max-w-4xl mx-auto bg-white border border-slate-300 shadow-md p-8 text-slate-900 font-sans">
        {/* Header */}
        <header className="text-center border-b-2 border-slate-900 pb-4 mb-5">
          <img
            src={logo}
            alt={appConfig.hotel?.name || "Hiwa Boutique Hotel"}
            className="w-16 h-16 object-contain mx-auto mb-2"
          />
          <div>
            <h1 className="text-xl font-bold tracking-wider text-slate-900 uppercase">
              HIWA BOUTIQUE HOTEL
            </h1>
            <p className="text-xs font-semibold text-slate-600 tracking-widest uppercase">
              A Business Boutique Hotel
            </p>
            <h2 className="text-base font-extrabold text-slate-900 mt-2 uppercase tracking-wide border-t border-b border-slate-400 py-1 inline-block px-4">
              GUEST REGISTRATION CERTIFICATE
            </h2>
          </div>
        </header>

        {/* Form Fields Grid */}
        <div className="space-y-4 text-sm text-slate-900">
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Guest Name :</span>
              <span className="font-semibold text-base px-2">{booking.customerName || "__________________"}</span>
            </div>
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Mobile :</span>
              <span className="font-semibold px-2">{booking.phoneNumber || "__________________"}</span>
            </div>

            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Arrival Date :</span>
              <span className="font-medium px-2">{formatDate(booking.checkInDate)}</span>
            </div>
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[130px]">Departure Date :</span>
              <span className="font-medium px-2">{formatDate(booking.checkOutDate)}</span>
            </div>

            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Arrival Time :</span>
              <span className="font-medium px-2">__________________</span>
            </div>
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[130px]">Departure Time :</span>
              <span className="font-medium px-2">__________________</span>
            </div>

            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Room No. :</span>
              <span className="font-medium px-2">{booking.roomType ? `${booking.roomType}` : "__________________"}</span>
            </div>
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[130px]">Room Tariff :</span>
              <span className="font-semibold px-2">₹ {Number(booking.amount || 0).toLocaleString("en-IN")}</span>
            </div>

            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[140px]">Number of Adults :</span>
              <span className="font-medium px-2">{booking.adults ?? "____"}</span>
            </div>
            <div className="border-b border-slate-400 pb-1 flex items-baseline">
              <span className="font-bold min-w-[110px]">Booking ID :</span>
              <span className="font-mono font-medium px-2">{booking.bookingId || "__________________"}</span>
            </div>
          </div>

          {/* Payment options checkboxes */}
          <div className="pt-2">
            <span className="font-bold block mb-2">Payment Options :</span>
            <div className="grid grid-cols-4 gap-2 text-xs">
              {paymentSources.map((source) => (
                <label key={source} className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" className="h-3.5 w-3.5 border-slate-500 rounded-none" />
                  <span>{source}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="border-b border-slate-400 pb-1 flex items-baseline pt-2">
            <span className="font-bold min-w-[150px]">PURPOSE OF VISIT :</span>
            <span className="w-full">_______________________________________________________________</span>
          </div>
        </div>

        {/* Terms & Conditions */}
        <div className="mt-6 pt-4 border-t border-slate-300">
          <h3 className="font-bold text-xs uppercase tracking-wide mb-2 text-slate-800 text-center">
            Terms & Conditions
          </h3>
          <ol className="list-decimal list-inside space-y-1 text-xs text-slate-700 leading-tight">
            <li>Check-In-Time: 12:00 Noon. Check-Out-Time: 11:00 a.m.</li>
            <li>
              Hotel Management is not responsible for your belongings and valuables like Money,
              Jewellery or any other left in the Room.
            </li>
            <li>100% advance to be paid at the time of check-in.</li>
            <li>
              A valid Photo ID has to be carried by every person above the age of 18 for the
              identification.
            </li>
            <li>
              Any extension of stay at the hotel is subject to availability of the rooms at the
              current ongoing rates and not at the rate at which the original booking was made.
            </li>
            <li>
              Hotel Reserves the right to deny check-in where customers are unable to provide valid
              government ID proof.
            </li>
          </ol>
        </div>

        {/* Signatures */}
        <div className="mt-10 pt-4 flex justify-between items-end text-xs font-semibold">
          <div>
            <p className="mb-8">Executive Sign. : ____________________</p>
            <p>Date : ____________________</p>
          </div>
          <div className="text-right">
            <p className="mb-8">Guest's Signature : ____________________</p>
            <p className="italic font-normal">I Agree to the above Terms & Conditions</p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default GrcForm;
